import React from "react";
import { Link } from "react-router-dom";

import { useT } from "../i18n";
import { useTemplateVars } from "../i18n/vars";
import { company, foundedYear } from "../config";
import { SERVICES } from "../content/services";
import { Wordmark } from "./ui/primitives";

// Not decoration. Legal name, registered address and a reachable contact are
// what a partner or a financial institution looks for when checking that a
// company is real, so all three stay visible on every page and legible rather
// than hidden in 10px grey.

function Column({ title, children }) {
  return (
    <div className="flex flex-col gap-4">
      <span className="font-caption text-[11px] uppercase tracking-[1.4px] text-fg-muted">{title}</span>
      <ul className="flex flex-col gap-[11px]">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }) {
  return (
    <li>
      <Link to={to} className="link-quiet font-body text-[14px]">
        {children}
      </Link>
    </li>
  );
}

export default function Footer() {
  const { t } = useT();
  const vars = useTemplateVars();
  const year = new Date().getFullYear();
  const yearLabel = year > foundedYear ? `${foundedYear}-${year}` : `${year}`;

  return (
    <footer className="mt-auto border-t border-line-subtle bg-surface-sunken">
      <div className="shell grid gap-12 pb-14 pt-16 sm:grid-cols-2 lg:grid-cols-[340px_repeat(3,1fr)] lg:gap-16 lg:pb-14 lg:pt-[72px]">
        <div className="flex flex-col gap-3.5">
          <Wordmark />
          <p className="max-w-xs font-body text-[14px] leading-[22px] text-fg-secondary">
            {t("footer.descriptor")}
          </p>
        </div>

        <Column title={t("footer.servicesTitle")}>
          {SERVICES.map((s) => (
            <FooterLink key={s.slug} to={`/${s.slug}`}>
              {t(`services.${s.slug}.navLabel`)}
            </FooterLink>
          ))}
        </Column>

        {/* No email address here on purpose. The contact route is the form;
            the address itself lives only in the Terms and Privacy Policy,
            which keeps it off the pages scrapers actually crawl. */}
        <Column title={t("footer.companyTitle")}>
          <FooterLink to="/contact">{t("footer.contact")}</FooterLink>
        </Column>

        <Column title={t("footer.legalTitle")}>
          <FooterLink to="/legal/terms">{t("footer.terms")}</FooterLink>
          <FooterLink to="/legal/privacy">{t("footer.privacy")}</FooterLink>
        </Column>
      </div>

      <div className="border-t border-line-subtle">
        <div className="shell flex flex-col gap-3 py-5 lg:flex-row lg:items-center lg:justify-between lg:gap-7">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-7">
            <span className="font-body text-[13px] text-fg-muted">
              &copy; {yearLabel} {company.legalName}. {t("footer.rights")}
            </span>
            {company.jurisdiction && (
              <span className="font-body text-[13px] text-fg-muted">
                {t("footer.registeredIn", vars)}
              </span>
            )}
          </div>
          {company.address && (
            <span className="font-mono text-[12px] text-fg-muted">{company.address}</span>
          )}
        </div>
      </div>
    </footer>
  );
}
